import { useCallback, useEffect } from 'react'
import { useDrawingArea, useSvgRef, useXAxis } from '@mui/x-charts/hooks'
import { lighten } from '@mui/system/colorManipulator'
import { useTheme } from '@mui/material/styles'
import { useQuery } from '../../../../hooks/useQuery'
import { useNavigate } from 'react-router-dom'
import { CUSTOM_DATE_END_DATE_ROUTE_PARAM, CUSTOM_DATE_START_DATE_ROUTE_PARAM } from './CustomDatesForm'
import { TIME_SELECTOR_ROUTE_PARAM } from './TimeSelector'

export const MouseZoom = () => {
    const rootRef = useSvgRef()
    const xAxisScale = useXAxis()
    const drawingArea = useDrawingArea()
    const theme = useTheme()
    const query = useQuery()
    const navigate = useNavigate()

    const navigateToZoom = useCallback(
        (start: number, end: number) => {
            const startDate = new Date(start).toISOString().split('T')[0]
            const endDate = new Date(end).toISOString().split('T')[0]

            query.set(CUSTOM_DATE_START_DATE_ROUTE_PARAM, startDate)
            query.set(CUSTOM_DATE_END_DATE_ROUTE_PARAM, endDate)
            query.set(TIME_SELECTOR_ROUTE_PARAM, 'custom')

            navigate(`?${query.toString()}`)
        },
        [navigate, query],
    )

    useEffect(() => {
        if (!rootRef.current) return

        const drawingAreaClientRect = rootRef.current?.getBoundingClientRect()

        let start: number | null = null
        let end: number | null = null

        const zoomElem = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        zoomElem.setAttribute('fill', lighten(theme.palette.primary.main, 0.2))
        zoomElem.setAttribute('opacity', '0.2')

        const getEnd = (e: MouseEvent) =>
            Math.max(Math.min(drawingAreaClientRect.left + drawingArea.width, e.clientX) - drawingAreaClientRect.left, 0)

        const handleMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return
            zoomElem.setAttribute('y', `${drawingArea.top}px`)
            zoomElem.setAttribute('height', `${drawingArea.height}px`)

            document.body.addEventListener('mouseup', handleMouseUp)
            document.body.addEventListener('mousemove', handleMouseMove)

            start = e.offsetX
            zoomElem.setAttribute('x', `${e.offsetX}`)
            rootRef.current?.appendChild(zoomElem)
        }

        const handleMouseMove = (e: MouseEvent) => {
            if (start === null) return

            end = getEnd(e)
            if (end < start) {
                zoomElem.setAttribute('x', `${end}`)
                zoomElem.setAttribute('width', `${start - end}`)
            } else {
                zoomElem.setAttribute('width', `${end - start}`)
            }
        }

        const handleMouseUp = (e: MouseEvent) => {
            end = getEnd(e)

            document.body.removeEventListener('mouseup', handleMouseUp)
            document.body.removeEventListener('mousemove', handleMouseMove)
            zoomElem.setAttribute('width', '0')
            rootRef.current?.removeChild(zoomElem)

            if (start == null || end === null) {
                console.warn('start or end was null', { start, end })
                return
            }

            const xValues = xAxisScale.data

            if (!xValues) {
                console.warn(`Failed to read xValues`, { xValues })
                return
            }

            const startIndex = Math.round((xValues.length - 1) * (start / drawingArea.width))
            const endIndex = Math.round((xValues.length - 1) * (end / drawingArea.width))

            if (Math.max(startIndex, endIndex) - Math.min(startIndex, endIndex) < 3) return // Don't zoom on < 2 days

            const startDate = xValues[startIndex]
            const endDate = xValues[endIndex]

            navigateToZoom(Math.min(startDate, endDate), Math.max(startDate, endDate))
        }

        const elem = rootRef.current

        elem.removeEventListener('mousedown', handleMouseDown)
        elem.addEventListener('mousedown', handleMouseDown, { once: true })

        return () => {
            elem.removeEventListener('mousedown', handleMouseDown)
        }
    }, [drawingArea.height, drawingArea.width, navigateToZoom, rootRef, theme.palette.primary.main, xAxisScale.data])

    return null
}
